import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Stethoscope, Lock, FileText, Download, AlertCircle, Loader2, Salad, Pill, TestTube2, MessageCircle, BookOpen, ChevronDown, AlertTriangle } from "lucide-react";
import { Toaster, toast } from "sonner";
import { CLINIC, LogoTile, doctorDisplay, rxLegalDisclaimer, doctorPhotoUrl, brandColor, BrandAccentStyle } from "../lib/clinic";
import { openWhatsAppRaw } from "../lib/whatsapp";
import { EDU_KITS, FOLLOWUP_SECTION } from "../lib/eduKits";

// Query param `?lang=mr|hi|en` (defaults to `en`) — sets the disclaimer
// language on the portal so a Marathi/Hindi patient reads it in their tongue.
const SUPPORTED_LANGS = new Set(["en", "mr", "hi"]);

export default function Portal() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const tLoc = searchParams.get("t") || undefined;   // MT-12B: tenant locator from the Care Kit link
  const initialLang = SUPPORTED_LANGS.has((searchParams.get("lang") || "").toLowerCase())
    ? searchParams.get("lang").toLowerCase()
    : "en";
  const [lang, setLang] = useState(initialLang);
  const [otp, setOtp] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Fire-and-forget analytics — log every landing on the portal page so we
  // can measure how many WhatsApp links actually get opened. Reuses the
  // same telemetry shape as diet-share events; never blocks the UX.
  useEffect(() => {
    if (!token) return;
    api.post("/portal/track", { token, t: tLoc, event: "opened" }).catch((e) => {
      console.debug("[portal-track opened silent]", e?.message || e);
    });
  }, [token, tLoc]);

  const verify = async () => {
    if (!otp || otp.length < 4) { toast.error("Enter the 4-digit OTP"); return; }
    setLoading(true);
    setErr("");
    try {
      const r = await api.post("/portal/verify", { token, otp, t: tLoc });
      setData(r.data);
      api.post("/portal/track", { token, t: tLoc, event: "verified" }).catch((e) => {
        console.debug("[portal-track verified silent]", e?.message || e);
      });
    } catch (e) {
      const msg = e.response?.data?.detail || "Verification failed";
      setErr(msg);
      api.post("/portal/track", { token, t: tLoc, event: "otp_failed" }).catch(() => {});
    }
    setLoading(false);
  };

  // Time-of-day greeting for the friendly welcome header. Uses the
  // patient's local browser time, which is always correct for our
  // Indian outpatient use-case. Localised per selected portal language.
  const GREETINGS = {
    en: { hello: "Hello", morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening",
          intro: `Here is your latest prescription and reports from ${CLINIC.name}.`,
          verifyTitle: "Verify access",
          verifyBody: "Enter the last 4 digits of your registered mobile number to view your latest prescription and reports.",
          otpPh: "Last 4 digits",
          verifyBtn: "View my prescription",
          followUp: "Follow-up",
          portalHeader: "Patient Portal",
          portalSub: "Read-only patient view" },
    mr: { hello: "नमस्कार", morning: "शुभ सकाळ", afternoon: "शुभ दुपार", evening: "शुभ संध्याकाळ",
          intro: `${CLINIC.name} कडून तुमची नवीनतम प्रिस्क्रिप्शन आणि अहवाल.`,
          verifyTitle: "प्रवेश तपासा",
          verifyBody: "तुमच्या नोंदणीकृत मोबाईल क्रमांकाचे शेवटचे ४ अंक भरून तुमची प्रिस्क्रिप्शन आणि अहवाल पहा.",
          otpPh: "शेवटचे ४ अंक",
          verifyBtn: "माझी प्रिस्क्रिप्शन पहा",
          followUp: "पुढील तपासणी",
          portalHeader: "रुग्ण पोर्टल",
          portalSub: "फक्त वाचनासाठी" },
    hi: { hello: "नमस्ते", morning: "सुप्रभात", afternoon: "शुभ दोपहर", evening: "शुभ संध्या",
          intro: `${CLINIC.name} से आपकी नवीनतम प्रिस्क्रिप्शन और रिपोर्ट।`,
          verifyTitle: "पहुँच सत्यापित करें",
          verifyBody: "अपने पंजीकृत मोबाइल नंबर के अंतिम 4 अंक दर्ज कर अपनी प्रिस्क्रिप्शन और रिपोर्ट देखें।",
          otpPh: "अंतिम 4 अंक",
          verifyBtn: "मेरी प्रिस्क्रिप्शन देखें",
          followUp: "फ़ॉलो-अप",
          portalHeader: "रोगी पोर्टल",
          portalSub: "केवल-पठन दृश्य" },
  };
  const t = GREETINGS[lang] || GREETINGS.en;
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5) return t.hello;
    if (h < 12) return t.morning;
    if (h < 17) return t.afternoon;
    if (h < 21) return t.evening;
    return t.hello;
  })();

  const firstName = (data?.patient?.name || "").trim().split(/\s+/)[0] || "";

  // Education kits — re-detected from the prescription + visit context so
  // patients who scan the printed care-kit QR see the same handouts inline.
  const detectedKits = (() => {
    if (!data?.prescription) return [];
    const kitCtx = {
      knownText: [
        ...(data?.visit?.known_case_of || []),
        data?.prescription?.diagnosis?.primary || "",
        data?.prescription?.diagnosis?.secondary || "",
      ].join(" ").toLowerCase(),
      egfr: (() => {
        const hit = (data?.visit?.investigations || []).find((i) => /egfr/i.test(i.test_name || ""));
        const v = parseFloat(hit?.result);
        return Number.isFinite(v) ? v : null;
      })(),
    };
    return EDU_KITS
      .map((k) => ({ kit: k, res: k.detect(data.prescription.medicines || [], kitCtx) }))
      .filter((d) => d.res.matched);
  })();

  return (
    <div className="min-h-screen bg-slate-50 brand-scope">
      <BrandAccentStyle scope="brand-scope" />
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center gap-3">
          <LogoTile className="h-9 w-9" padding="p-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-sm font-semibold leading-tight truncate">{CLINIC.name} — {t.portalHeader}</div>
            <div className="text-[10px] tracking-[0.1em] uppercase text-slate-500 leading-none mt-1">{t.portalSub}</div>
          </div>
          {/* Language toggle — sticky in the header so the patient can flip
              language any time. Persisted in the URL search params so the
              choice survives an OTP refresh or a bookmark. */}
          <div className="inline-flex rounded-full border border-slate-200 overflow-hidden text-[11px] shrink-0" data-testid="portal-lang-toggle">
            {[
              { k: "en", label: "EN" },
              { k: "mr", label: "मराठी" },
              { k: "hi", label: "हिंदी" },
            ].map((o) => (
              <button
                key={o.k}
                type="button"
                onClick={() => {
                  setLang(o.k);
                  // Reflect the choice in the URL so a bookmarked reload
                  // remembers it (also lets the doctor screen-share the URL
                  // with the exact language pre-selected).
                  const usp = new URLSearchParams(window.location.search);
                  if (o.k === "en") usp.delete("lang"); else usp.set("lang", o.k);
                  const qs = usp.toString();
                  window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
                }}
                className={`px-2.5 py-1 transition-colors ${lang === o.k ? "bg-pink-600 text-white font-semibold" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid={`portal-lang-${o.k}`}
                lang={o.k}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {!data && (
        <div className="max-w-md mx-auto p-6 mt-16 bg-white border border-slate-200 rounded-md" data-testid="portal-otp-panel">
          <div className="flex items-center gap-2">
            <Lock size={18} className="text-pink-600" />
            <div className="font-heading text-lg font-semibold" lang={lang}>{t.verifyTitle}</div>
          </div>
          <p className="mt-3 text-sm text-slate-600" lang={lang}>{t.verifyBody}</p>
          <div className="mt-4">
            <Label className="overline">OTP</Label>
            <Input
              value={otp}
              maxLength={4}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => e.key === "Enter" && verify()}
              className="mt-1 text-center text-2xl h-12 tracking-widest tabular"
              placeholder={t.otpPh}
              data-testid="portal-otp-input"
            />
          </div>
          {err && (
            <div className="mt-3 alert-critical rounded-md px-3 py-2 text-xs flex items-center gap-2" data-testid="portal-error">
              <AlertCircle size={12} /> {err}
            </div>
          )}
          <Button
            onClick={verify}
            disabled={loading || otp.length < 4}
            className="mt-4 w-full h-11 bg-pink-600 hover:bg-pink-700 text-white gap-2"
            data-testid="portal-verify-btn"
          >
            {loading ? <><Loader2 className="animate-spin" size={14} /> Verifying…</> : t.verifyBtn}
          </Button>
          <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">
            This link expires automatically for your security. Never share this link with others —
            anyone with the link and your OTP can view this record.
          </p>
        </div>
      )}

      {data && (
        <div className="max-w-3xl mx-auto p-6 mt-6 space-y-4">
          {/* Friendly welcome header — first name + time-of-day greeting so
              the portal feels personal, not clinical. Falls back to the
              patient's full name if the name split returns nothing. */}
          <div className="bg-gradient-to-br from-pink-500 to-pink-700 text-white rounded-md p-6 shadow-sm" data-testid="portal-welcome">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-pink-100/80 font-semibold" lang={lang}>
                  {greeting}
                </div>
                <div className="font-heading text-3xl font-semibold mt-1" data-testid="portal-welcome-name">
                  {firstName || data.patient?.name || "Welcome"} <span className="inline-block ml-1">👋</span>
                </div>
                <div className="text-[12px] text-pink-100/90 mt-1" lang={lang}>
                  {t.intro}
                </div>
              </div>
              <div className="flex-shrink-0 text-center" data-testid="portal-doctor-photo">
                <img
                  src={doctorPhotoUrl()}
                  alt={CLINIC.doctorName}
                  className="h-16 w-16 rounded-full object-cover object-top ring-2 ring-white/70 mx-auto"
                />
                <div className="text-[10px] text-pink-100/90 mt-1.5 font-medium leading-tight max-w-[88px]">{CLINIC.doctorName}</div>
              </div>
            </div>
            <div className="text-[11px] text-pink-100/80 mt-3 tabular border-t border-white/20 pt-2" data-testid="portal-patient-id">
              {data.patient?.patient_id} · {data.patient?.age} yr / {data.patient?.sex}
            </div>
          </div>

          {data.visit?.vitals && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="section-title">Vitals from last visit</div>
              <div className="grid grid-cols-3 gap-3 mt-3 text-sm tabular">
                <Kv label="BP" v={`${data.visit.vitals.sbp || "-"}/${data.visit.vitals.dbp || "-"}`} />
                <Kv label="Pulse" v={data.visit.vitals.pulse || "-"} />
                <Kv label="BMI" v={data.visit.vitals.bmi || "-"} />
                <Kv label="SpO₂" v={data.visit.vitals.spo2 || "-"} />
                <Kv label="RBS" v={data.visit.vitals.random_sugar || "-"} />
                <Kv label="Temp" v={data.visit.vitals.temperature || "-"} />
              </div>
            </div>
          )}

          {data.prescription && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="section-title flex items-center gap-2">
                <FileText size={16} /> Latest prescription
              </div>
              <div className="text-xs text-slate-500 mt-1 tabular">
                {new Date(data.prescription.created_at).toLocaleDateString()} · {doctorDisplay(CLINIC.doctorName)}
              </div>
              {data.prescription.diagnosis?.primary && (
                <div className="mt-3 text-sm">
                  <span className="text-slate-500">Diagnosis:</span> <span className="font-medium">{data.prescription.diagnosis.primary}</span>
                </div>
              )}
              <table className="w-full text-sm mt-3">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2">Medicine</th><th>Dose</th><th>Frequency</th><th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.prescription.medicines || []).map((m, i) => (
                    <tr key={i} className={`border-b border-slate-100 align-top ${m.type === "insulin" ? "bg-amber-50/60" : ""}`}>
                      <td className="py-2 pr-2">
                        <div className="font-medium">{m.brand_name} <span className="text-slate-500 font-normal">{m.strength}</span></div>
                        <div className="text-[11px] text-slate-500">{m.generic_name}</div>
                        {m.type === "insulin" && m.insulin_regimen && (
                          <div className="text-[10px] text-slate-700 mt-0.5 tabular">
                            {["morning", "afternoon", "evening", "bedtime"].filter((k) => m.insulin_regimen[k]).map((k) => `${k[0].toUpperCase() + k.slice(1)} ${m.insulin_regimen[k]}U`).join(" · ")}
                          </div>
                        )}
                        {m.instructions && <div className="text-[10px] text-slate-500 italic">{m.instructions}</div>}
                      </td>
                      <td className="py-2 pr-2 tabular">{m.dose}</td>
                      <td className="py-2 pr-2">{m.frequency} {m.timing ? `(${m.timing})` : ""}</td>
                      <td className="py-2 pr-2">{m.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.prescription.follow_up && (
                <div className="mt-3 text-sm">
                  <span className="text-slate-500">Follow-up:</span> <span className="font-medium">{data.prescription.follow_up}</span>
                </div>
              )}

              {/* Legal expiry / dispensing disclaimer — required on every
                  patient-facing prescription view. Small yet bold per the
                  clinical governance guideline shared by Dr. Shinde.
                  Language is driven by the ?lang= query param on the URL. */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-[11px] font-bold text-slate-700 leading-relaxed" data-testid="rx-legal-disclaimer" lang={lang}>
                  {rxLegalDisclaimer(lang)}
                </p>
              </div>
            </div>
          )}

          {detectedKits.length > 0 && (
            <div className="space-y-3" data-testid="portal-kits">
              <div className="section-title flex items-center gap-2 px-1">
                <BookOpen size={16} /> {lang === "mr" ? "तुमची काळजी किट" : lang === "hi" ? "आपकी देखभाल किट" : "Your care kits"}
              </div>
              {detectedKits.map(({ kit }) => (
                <PortalKitCard key={kit.id} kit={kit} lang={lang} />
              ))}
            </div>
          )}

          {data.reports?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="section-title">Your reports</div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.reports.map((r) => (
                  <a key={r.report_id} href={`${API}${r.url.replace("/api", "")}`} target="_blank" rel="noreferrer" className="border border-slate-200 rounded-md p-3 flex items-center justify-between hover:border-pink-300 hover:bg-pink-50/40" data-testid={`portal-report-${r.report_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.file_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.category} · {new Date(r.uploaded_at).toLocaleDateString()}</div>
                    </div>
                    <Download size={16} className="text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {CLINIC.services_whatsapp && (
            <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="portal-services">
              <div className="section-title flex items-center gap-2">
                <MessageCircle size={16} /> Request a service
              </div>
              <div className="text-xs text-slate-500 mt-1">
                One tap opens WhatsApp — our team will get back to you shortly.
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <ServiceBtn
                  icon={Salad}
                  label="Dietician Diet Plan"
                  testid="portal-service-diet"
                  onClick={() => openWhatsAppRaw(CLINIC.services_whatsapp,
                    `Hello, I am ${data.patient?.name || ""} (${data.patient?.patient_id || ""}). I would like to request a Dietician Diet Plan.`)}
                />
                <ServiceBtn
                  icon={Pill}
                  label="Doorstep Medicine Delivery"
                  testid="portal-service-medicine"
                  onClick={() => openWhatsAppRaw(CLINIC.services_whatsapp,
                    `Hello, I am ${data.patient?.name || ""} (${data.patient?.patient_id || ""}). I would like to request Doorstep Medicine Delivery.`)}
                />
                <ServiceBtn
                  icon={TestTube2}
                  label="Doorstep Lab Sample Collection"
                  testid="portal-service-lab"
                  onClick={() => openWhatsAppRaw(CLINIC.services_whatsapp,
                    `Hello, I am ${data.patient?.name || ""} (${data.patient?.patient_id || ""}). I would like to request Doorstep Lab Sample Collection.`)}
                />
              </div>
            </div>
          )}

          <footer className="pt-2 text-[11px] text-slate-500 leading-relaxed">
            This is a read-only view. For any questions, please contact {CLINIC.name} directly. If you did not request this link, ignore it — it expires automatically.
          </footer>
        </div>
      )}
    </div>
  );
}

// Read-only, expandable education-kit handout for the patient portal.
// Bilingual EN/मराठी (Hindi portal viewers fall back to English content).
function PortalKitCard({ kit, lang }) {
  const [open, setOpen] = useState(false);
  const L = (b) => (lang === "mr" ? b.mr || b.en : b.en);
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid={`portal-kit-${kit.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left"
        data-testid={`portal-kit-toggle-${kit.id}`}
      >
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{L(kit.title)}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {lang === "mr" ? "वाचण्यासाठी टॅप करा — तुमच्या उपचारासाठी खास तयार केलेले" : "Tap to read — prepared specially for your treatment"}
          </div>
        </div>
        <ChevronDown size={18} className={`text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div
            className="rounded-lg p-3 flex items-center gap-3 text-white"
            style={{ backgroundColor: brandColor() }}
            data-testid={`portal-kit-brand-header-${kit.id}`}
          >
            <LogoTile className="h-10 w-10 shrink-0" padding="p-1" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-extrabold leading-tight">{CLINIC.name}</div>
              <div className="text-[10px] opacity-90">
                {doctorDisplay(CLINIC.doctorName)}{CLINIC.specialties ? ` · ${CLINIC.specialties}` : ""}
              </div>
            </div>
            <img src={doctorPhotoUrl()} alt="" className="h-10 w-10 rounded-full object-cover border-2 border-white/70 shrink-0" />
          </div>
          {kit.sections.map((s) => (
            <section key={s.id} className={`rounded-lg border p-3 ${s.tone === "danger" ? "border-red-300 bg-red-50/50" : "border-slate-200 bg-slate-50/50"}`}>
              <div className={`text-[13px] font-bold mb-1.5 ${s.tone === "danger" ? "text-red-800" : "text-slate-800"}`}>{L(s.title)}</div>
              <ul className="space-y-1.5">
                {s.items.map((it, i) => (
                  <li key={i} className="text-[12px] leading-snug text-slate-700 flex gap-2">
                    <span className={s.tone === "danger" ? "text-red-600" : "text-emerald-600"}>{s.tone === "danger" ? "✗" : "✓"}</span>
                    <span>{L(it)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <section className="rounded-lg border-2 border-red-400 bg-red-50 p-3">
            <div className="text-[13px] font-bold text-red-800 mb-1.5 flex items-center gap-1.5">
              <AlertTriangle size={14} /> {L(kit.danger.title)}
            </div>
            <ul className="space-y-1.5">
              {kit.danger.items.map((it, i) => (
                <li key={i} className="text-[12px] leading-snug text-red-900 flex gap-2"><span>⚠</span><span>{L(it)}</span></li>
              ))}
            </ul>
          </section>
          <section className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
            <div className="text-[13px] font-bold text-indigo-900 mb-1.5">{L(FOLLOWUP_SECTION.title)}</div>
            <ul className="space-y-1.5">
              {FOLLOWUP_SECTION.items.map((it, i) => (
                <li key={i} className="text-[12px] leading-snug text-slate-700 flex gap-2"><span className="text-indigo-600">→</span><span>{L(it)}</span></li>
              ))}
            </ul>
          </section>
          <div
            className="rounded-lg border p-3 text-[11px] leading-snug"
            style={{ borderColor: brandColor() }}
            data-testid={`portal-kit-brand-footer-${kit.id}`}
          >
            <div className="font-extrabold text-[12px]" style={{ color: brandColor() }}>{CLINIC.name}</div>
            {CLINIC.address && <div className="text-slate-600 mt-0.5">📍 {CLINIC.address}</div>}
            {(CLINIC.phones || []).length > 0 && <div className="text-slate-600">📞 {CLINIC.phones.join(" · ")}</div>}
            <div className="text-slate-600 flex flex-wrap gap-x-3">
              {CLINIC.website && <span>🌐 {CLINIC.website}</span>}
              {CLINIC.timings && <span>🕒 {CLINIC.timings}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-2.5 border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-100 rounded-md px-3 py-3 text-left transition-colors"
    >
      <Icon size={18} className="text-emerald-700 flex-shrink-0" />
      <span className="text-xs font-medium text-slate-800 leading-snug">{label}</span>
    </button>
  );
}

function Kv({ label, v }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="mt-1 font-medium tabular">{v}</div>
    </div>
  );
}
